/**
 * Iter-246c — Admin Settings hub (configuration only).
 *
 * Per Mel's feedback (Iter-246c), this surface is reserved for platform
 * CONFIGURATION items — anything that's a one-time setting and not a
 * daily operational workflow. Operational tools (IDV Review, GGF Audit,
 * Recap Compliance, Timesheets, Certifications) live under the
 * Operations Center, where admins can reach them in one click from the
 * main admin navigation.
 *
 * Sections kept here:
 *   • Account & Profile     (Admin Profile, Profile Quality)
 *   • Platform Configuration (GGF Pass Threshold, Welcome Videos)
 *   • Integrations & API     (Integrations console, Broadcast Templates)
 *   • Session                 (Logout)
 *
 * Mobile-first layout: stacks vertically below `md`, 2-col on `md`+.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  ArrowLeft, UserCircle, Settings as SettingsIcon, ShieldCheck,
  Video, Plug, Mail, LogOut, ChevronRight, BadgeCheck, Zap, Building2, Link2, Bell, Activity, Bug, Scale, AlertTriangle, ImageOff, Ban,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import LinkedAccountsPanel from '../../components/LinkedAccountsPanel';

const Row = ({ icon: Icon, label, sub, to, onClick, testid, danger = false }) => {
  const navigate = useNavigate();
  const handle = onClick || (() => navigate(to));
  return (
    <button
      onClick={handle}
      data-testid={testid}
      className={`w-full flex items-center gap-3 p-4 rounded-xl border bg-white text-left transition-all ${
        danger
          ? 'border-rose-200 hover:border-rose-400 hover:bg-rose-50'
          : 'border-slate-200 hover:border-cyan-400 hover:bg-cyan-50/40 hover:shadow-sm'
      }`}
    >
      <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${
        danger ? 'bg-rose-100 text-rose-600' : 'bg-cyan-50 text-cyan-600 ring-1 ring-cyan-100'
      }`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-semibold ${danger ? 'text-rose-700' : 'text-slate-900'}`}>{label}</div>
        {sub && <div className="text-xs text-slate-500 mt-0.5 truncate">{sub}</div>}
      </div>
      <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
    </button>
  );
};

const Group = ({ title, children }) => (
  <section className="space-y-2">
    <h2 className="text-[11px] font-bold uppercase tracking-wider text-slate-500 px-1">{title}</h2>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>
  </section>
);

const AdminSettings = () => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="admin-settings-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <SettingsIcon className="w-6 h-6 text-cyan-600" />
            Account Settings
          </h1>
        </div>

        <p className="text-sm text-slate-600 px-1">
          Signed in as <strong>{user?.email}</strong>
          {user?.admin_role && (
            <span className="ml-2 text-[11px] uppercase tracking-wider text-cyan-700 bg-cyan-50 border border-cyan-200 px-2 py-0.5 rounded-full">
              {user.admin_role.replace('_', ' ')}
            </span>
          )}
        </p>

        {/* Quick link to operational tools, since admins occasionally land
            here looking for IDV Review or GGF Audit. */}
        <div
          className="rounded-2xl border border-amber-200 bg-amber-50/50 p-4 flex items-center gap-3"
          data-testid="settings-ops-banner"
        >
          <Zap className="w-5 h-5 text-amber-600 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-amber-900">Looking for daily workflows?</div>
            <p className="text-xs text-amber-800/80">
              IDV Review, GGF Audit, Timesheets, and Recap Compliance live in the Operations Center.
            </p>
          </div>
          <Button
            size="sm"
            className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
            onClick={() => navigate('/admin/operations')}
            data-testid="settings-goto-ops"
          >
            Open Operations Center
          </Button>
        </div>

        <Group title="Account & Profile">
          <Row
            icon={UserCircle}
            label="Edit My Profile"
            sub="Display name, bio, avatar"
            to="/admin/profile"
            testid="admin-settings-profile"
          />
          <Row
            icon={BadgeCheck}
            label="Profile Quality Audit"
            sub="See profile completeness & gaps"
            to="/admin/profile-quality"
            testid="admin-settings-profile-quality"
          />
        </Group>

        <Group title="Platform Configuration">
          <Row
            icon={ShieldCheck}
            label="GGF Pass Threshold"
            sub="Adjust the GGF certification pass mark"
            to="/admin/ggf/settings"
            testid="admin-settings-ggf"
          />
          <Row
            icon={Video}
            label="Welcome Video Settings"
            sub="Onboarding video URLs & defaults"
            to="/admin/settings/videos"
            testid="admin-settings-videos"
          />
          <Row
            icon={Bell}
            label="Notification Preferences"
            sub="Per-event toggles for in-app, email & SMS"
            to="/admin/notification-preferences"
            testid="admin-settings-notification-prefs"
          />
          <Row
            icon={Activity}
            label="Delivery Health"
            sub="Live email · SMS · in-app delivery status & failures"
            to="/admin/delivery-health"
            testid="admin-settings-delivery-health"
          />
          <Row
            icon={Scale}
            label="Settlement Challenges"
            sub="Contractor hours / settlement disputes — review & resolve"
            to="/admin/settlement-challenges"
            testid="admin-settings-settlement-challenges"
          />
          <Row
            icon={AlertTriangle}
            label="Overdue Settlements"
            sub="Settlements past their expected (Friday) date — proactive queue"
            to="/admin/overdue-settlements"
            testid="admin-settings-overdue-settlements"
          />
          <Row
            icon={Bug}
            label="Frontend Errors"
            sub="Client-side crashes & failed page loads, triage & resolve"
            to="/admin/frontend-errors"
            testid="admin-settings-frontend-errors"
          />
          <Row
            icon={ImageOff}
            label="Flagged Profile Images"
            sub="Auto-detected prohibited logo / copied placeholder photos"
            to="/admin/flagged-images"
            testid="admin-settings-flagged-images"
          />
          <Row
            icon={Ban}
            label="Client Block Talent"
            sub="Clients who have blocked talent — active blocks & history"
            to="/admin/client-talent-blocks"
            testid="admin-settings-client-blocks"
          />
          <Row
            icon={ShieldCheck}
            label="Gig Code of Conduct"
            sub="Publish versioned policies & audit per-gig acceptances"
            to="/admin/code-of-conduct"
            testid="admin-settings-code-of-conduct"
          />
        </Group>

        <Group title="Integrations & API">
          <Row
            icon={Plug}
            label="Integrations"
            sub="Xero · Stripe · Yardstik · SendGrid"
            to="/admin/integrations"
            testid="admin-settings-integrations"
          />
          <Row
            icon={Building2}
            label="Client Business Rules"
            sub="Locked rates, role remaps, invoice grouping"
            to="/admin/client-business-rules"
            testid="admin-settings-client-business-rules"
          />
          <Row
            icon={Mail}
            label="Broadcast Templates"
            sub="Manage transactional email copy"
            to="/admin/broadcast-templates"
            testid="admin-settings-broadcast"
          />
        </Group>

        <Group title="Session">
          <Row
            icon={LogOut}
            label="Logout"
            sub="Sign out of the admin console"
            onClick={() => { logout(); navigate('/login'); }}
            testid="admin-settings-logout"
            danger
          />
        </Group>
      </div>
    </div>
  );
};

export default AdminSettings;
